import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import { useApp } from '../state.js';

export interface ReviewItem {
  id: string;
  rows: number[];
  kind: 'task' | 'event' | 'note' | 'ignore';
  transcription: string;
  title: string;
  dueDate: string | null;
  dueTime: string | null;
  durationMin: number | null;
  priority: 1 | 2 | 3 | 4;
  assignee: string | null;
  project: string | null;
  projectId: string | null;
  labels: string[];
  confidence: 'alta' | 'media' | 'baixa';
  doubt: string | null;
  private: boolean;
  relation: 'atualiza' | 'novo' | 'igual' | 'concluida' | 'adiada' | null;
  accepted: boolean;
}

interface Props {
  runId: string;
  items: ReviewItem[];
  onFocusRows(rows: number[]): void;
  onDone(message: string): Promise<void> | void;
}

const KIND_LABEL = { task: 'Tarefa', event: 'Compromisso', note: 'Anotação', ignore: 'Ignorar' } as const;

/** Linha no formato de adição rápida do Todoist, para colar à mão enquanto a
 *  conta não está conectada (SPEC, aceite da Fase 3). */
function toQuickAdd(i: ReviewItem): string {
  const parts = [i.title];
  if (i.private) parts.push('@privado');
  if (i.project) parts.push(`#${i.project.replace(/\s+/g, '')}`);
  if (i.assignee) parts.push(`@${i.assignee.replace(/\s+/g, '')}`);
  for (const l of i.labels) parts.push(`@${l.replace(/\s+/g, '')}`);
  if (i.priority < 4) parts.push(`p${i.priority}`);
  if (i.dueDate) parts.push(i.dueTime ? `${i.dueDate} ${i.dueTime}` : i.dueDate);
  return parts.join(' ');
}

export default function ReviewPanel({ runId, items: initial, onFocusRows, onDone }: Props): JSX.Element {
  const showToast = useApp((s) => s.showToast);
  const [items, setItems] = useState<ReviewItem[]>(initial);
  const [focused, setFocused] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [connected, setConnected] = useState(true);

  useEffect(() => {
    void api.get<{ connected: boolean; projects: { id: string; name: string }[] }>('/api/todoist/status')
      .then((r) => { setProjects(r.projects); setConnected(r.connected); })
      .catch(() => setConnected(false));
  }, []);

  const actions = useMemo(() => items.filter((i) => i.kind === 'task' || i.kind === 'event'), [items]);
  const rest = useMemo(() => items.filter((i) => i.kind === 'note' || i.kind === 'ignore'), [items]);
  const toSend = actions.filter((i) => i.accepted).length;
  const doubts = items.filter((i) => i.doubt).length;

  function patch(id: string, change: Partial<ReviewItem>): void {
    setItems((list) => list.map((i) => (i.id === id ? { ...i, ...change } : i)));
  }

  async function apply(mode: 'send' | 'mark' | 'discard'): Promise<void> {
    setBusy(true);
    try {
      const res = await api.post<{ sent?: number; failed?: number }>(`/api/review/${runId}/apply`, {
        mode,
        items: items.map((i) => ({
          id: i.id, kind: i.kind, title: i.title, dueDate: i.dueDate, dueTime: i.dueTime,
          durationMin: i.durationMin, priority: i.priority, assignee: i.assignee,
          projectId: i.projectId, project: i.project, labels: i.labels,
          accepted: i.accepted && (i.kind === 'task' || i.kind === 'event'),
        })),
      });
      const msg =
        mode === 'discard' ? 'Leitura descartada. As linhas continuam novas.'
        : mode === 'mark' ? 'Linhas marcadas como lidas. Nada foi para o Todoist.'
        : res.failed ? `${res.sent ?? 0} enviadas, ${res.failed} com erro — vou tentar de novo sozinho.`
        : `${res.sent ?? 0} tarefa(s) criadas no Todoist.`;
      await onDone(msg);
    } catch {
      showToast('Não consegui concluir. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  async function copyQuickAdd(): Promise<void> {
    const text = actions.filter((i) => i.accepted).map(toQuickAdd).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${actions.filter((i) => i.accepted).length} linha(s) copiadas. Cole no Todoist.`);
    } catch {
      showToast('Não consegui copiar. Selecione o texto na transcrição.');
    }
  }

  const card = (i: ReviewItem): JSX.Element => (
    <div
      key={i.id}
      className={`item ${focused === i.id ? 'focused' : ''}`}
      onPointerEnter={() => { setFocused(i.id); onFocusRows(i.rows); }}
      onFocus={() => { setFocused(i.id); onFocusRows(i.rows); }}
      onDoubleClick={() => patch(i.id, { accepted: true })}
    >
      <div className="row wrap">
        <span className="badge">L{i.rows.join(', L')}</span>
        <select value={i.kind} onChange={(e) => patch(i.id, { kind: e.target.value as ReviewItem['kind'] })} style={{ width: 'auto' }}>
          {Object.entries(KIND_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        {i.confidence === 'baixa' ? <span className="badge warn">letra difícil</span> : null}
        {/* A marca (p) precisa ser visível aqui: é a única chance de pegar uma
            anotação pessoal antes de ela virar candidata a e-mail (SPEC §15.4). */}
        {i.private ? <span className="badge" title="Nunca entra em ata nem e-mail">privada</span> : null}
        {i.relation === 'concluida' ? <span className="badge ok">X = feito</span> : null}
        {i.relation === 'adiada' ? <span className="badge warn">&gt; = adiar</span> : null}
        <span className="spacer" />
        {i.kind === 'task' || i.kind === 'event' ? (
          <label className="row small" style={{ gap: 4 }}>
            <input
              type="checkbox" style={{ width: 20, minHeight: 20 }} checked={i.accepted}
              onChange={(e) => patch(i.id, { accepted: e.target.checked })}
            />
            Criar
          </label>
        ) : null}
      </div>

      <input value={i.title} onChange={(e) => patch(i.id, { title: e.target.value })} />
      <div className="transcript">{i.transcription || '—'}</div>
      {i.doubt ? <div className="doubt">{i.doubt}</div> : null}

      {i.kind === 'task' || i.kind === 'event' ? (
        <div className="fields">
          <label>Data
            <input type="date" value={i.dueDate ?? ''} onChange={(e) => patch(i.id, { dueDate: e.target.value || null })} />
          </label>
          <label>Hora
            <input type="time" value={i.dueTime ?? ''} onChange={(e) => patch(i.id, { dueTime: e.target.value || null })} />
          </label>
          <label>Prioridade
            <select value={i.priority} onChange={(e) => patch(i.id, { priority: Number(e.target.value) as 1 })}>
              <option value={1}>P1 urgente</option>
              <option value={2}>P2</option>
              <option value={3}>P3</option>
              <option value={4}>P4 normal</option>
            </select>
          </label>
          <label>Projeto
            <select
              value={i.projectId ?? ''}
              onChange={(e) => {
                const p = projects.find((x) => x.id === e.target.value);
                patch(i.id, { projectId: p?.id ?? null, project: p?.name ?? null });
              }}
            >
              <option value="">Inbox</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label>Responsável
            <input value={i.assignee ?? ''} onChange={(e) => patch(i.id, { assignee: e.target.value || null })} />
          </label>
          <label>Duração (min)
            <input
              type="number" min={0} step={5} value={i.durationMin ?? ''}
              onChange={(e) => patch(i.id, { durationMin: e.target.value ? Number(e.target.value) : null })}
            />
          </label>
        </div>
      ) : null}
    </div>
  );

  return (
    <aside className="review">
      <header className="row wrap">
        <strong>Revisão</strong>
        <span className="badge">{toSend} para criar</span>
        {doubts ? <span className="badge warn">{doubts} com dúvida</span> : null}
        {!connected ? <span className="badge danger">Todoist não conectado</span> : null}
      </header>

      <div className="list">
        {actions.map(card)}
        {rest.length ? (
          <>
            <p className="muted small" style={{ margin: '4px 0 0' }}>Anotações e ignoradas</p>
            {rest.map(card)}
          </>
        ) : null}
        {!items.length ? <p className="muted">A leitura não devolveu nada para esta página.</p> : null}
      </div>

      <footer>
        {connected ? (
          <button className="primary" disabled={busy || !toSend} onClick={() => void apply('send')}>
            Enviar ao Todoist ({toSend})
          </button>
        ) : (
          <button className="primary" disabled={busy || !toSend} onClick={() => void copyQuickAdd()}>
            Copiar no formato do Todoist ({toSend})
          </button>
        )}
        <button disabled={busy} onClick={() => void apply('mark')}>Só marcar como lido</button>
        <button disabled={busy} className="danger" onClick={() => void apply('discard')}>Descartar</button>
      </footer>
    </aside>
  );
}
